import React, { useState, useEffect } from "react";
import api from "../../../api";
import "./HeldItemQuickSwap.css";

const HeldItemQuickSwap = ({ isVisible, onClose, onSwapComplete }) => {
  const [loading, setLoading] = useState(true);
  const [swapping, setSwapping] = useState(false);
  const [equipData, setEquipData] = useState(null);
  const [storeItems, setStoreItems] = useState([]);
  const [inventoryMap, setInventoryMap] = useState({});
  const [availableItems, setAvailableItems] = useState([]);

  // Fetch equipment and available handheld items
  useEffect(() => {
    if (isVisible) {
      fetchData();
    }
  }, [isVisible]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [equipRes, storeRes, inventoryRes] = await Promise.all([
        api.get("/v1/equipment", { withCredentials: true }),
        api.get("/v1/store", { withCredentials: true }),
        api.get("/v1/inventory", { withCredentials: true }),
      ]);

      setEquipData(equipRes.data);
      setStoreItems(storeRes.data);

      // Create inventory mapping
      const invMap = inventoryRes.data.reduce((acc, inv) => {
        if (inv.amount > 0 || inv.stack_amount > 0) {
          const key = inv.item ?? inv.id;
          acc[key] = inv;
        }
        return acc;
      }, {});
      setInventoryMap(invMap);

      // Get all equipable items
      setAvailableItems(equipRes.data.equipable || []);
    } catch (error) {
      console.error("Error fetching equipment data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSwap = async (itemId) => {
    if (swapping) return;
    
    setSwapping(true);
    try {
      const charmIds = new Set([193, 194, 121]);
      const equipableCharm = charmIds.has(itemId)
        ? (equipData?.equipable || []).find((item) => item.id === itemId)
        : null;

      if (equipableCharm) {
        const maxStack = equipableCharm.max_stack_amount > 0
          ? equipableCharm.max_stack_amount
          : equipableCharm.amount;
        const remaining = Math.max(
          0,
          maxStack - (equipableCharm.stack_amount || 0),
        );

        for (let i = 0; i < remaining; i += 1) {
          await api.post("/v1/equip", { item: itemId }, { withCredentials: true });
        }
      } else {
        await api.post("/v1/equip", { item: itemId }, { withCredentials: true });
      }
      
      // Update both equipment and inventory data
      const [equipRes, inventoryRes] = await Promise.all([
        api.get("/v1/equipment", { withCredentials: true }),
        api.get("/v1/inventory", { withCredentials: true })
      ]);
      
      setEquipData(equipRes.data);
      
      // Update inventory mapping
      const invMap = inventoryRes.data.reduce((acc, inv) => {
        if (inv.amount > 0 || inv.stack_amount > 0) {
          const key = inv.item ?? inv.id;
          acc[key] = inv;
        }
        return acc;
      }, {});
      setInventoryMap(invMap);
      
      // Update available items
      setAvailableItems(equipRes.data.equipable || []);
      
      if (onSwapComplete) {
        onSwapComplete();
      }
    } catch (error) {
      console.error("Error swapping held item:", error);
    } finally {
      setSwapping(false);
    }
  };

  const handleUnequip = async (itemId) => {
    if (swapping) return;
    
    // Prevent unequipping the equipment ring (item ID 28)
    if (itemId === 28) {
      console.log("Cannot unequip the equipment ring");
      return;
    }

    setSwapping(true);
    try {
      const charmIds = new Set([193, 194, 121]);
      const equipableCharm = charmIds.has(itemId)
        ? (equipData?.equipable || []).find((item) => item.id === itemId)
        : null;

      if (equipableCharm && equipableCharm.stack_amount) {
        for (let i = 0; i < equipableCharm.stack_amount; i += 1) {
          await api.post("/v1/unequip", { item: itemId }, { withCredentials: true });
        }
      } else {
        await api.post("/v1/unequip", { item: itemId }, { withCredentials: true });
      }
      
      // Update both equipment and inventory data
      const [equipRes, inventoryRes] = await Promise.all([
        api.get("/v1/equipment", { withCredentials: true }),
        api.get("/v1/inventory", { withCredentials: true })
      ]);
      
      setEquipData(equipRes.data);
      
      // Update inventory mapping
      const invMap = inventoryRes.data.reduce((acc, inv) => {
        if (inv.amount > 0 || inv.stack_amount > 0) {
          const key = inv.item ?? inv.id;
          acc[key] = inv;
        }
        return acc;
      }, {});
      setInventoryMap(invMap);
      
      // Update available items
      setAvailableItems(equipRes.data.equipable || []);
      
      if (onSwapComplete) {
        onSwapComplete();
      }
    } catch (error) {
      console.error("Error unequipping item:", error);
    } finally {
      setSwapping(false);
    }
  };

  // Create mapping from item name to item details
  const storeItemsByName = storeItems.reduce((acc, item) => {
    acc[item.name] = item;
    return acc;
  }, {});

  if (!isVisible) return null;

  return (
    <div 
      className="game-popup-overlay"
      onClick={onClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px 10px',
        boxSizing: 'border-box',
        zIndex: 1050
      }}
    >
      <div 
        className="equipment-manager-popup"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '90%',
          maxWidth: 500,
          maxHeight: '90vh',
          background: 'rgba(29, 34, 46, 0.95)',
          color: '#fff',
          borderRadius: 12,
          backdropFilter: 'blur(10px)',
          border: '2px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <div style={{
          padding: '16px 20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'rgba(0, 0, 0, 0.2)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '20px' }}>⚒️</span>
            <h3 style={{ margin: 0, fontSize: '18px', fontWeight: 'bold' }}>Equipment Manager</h3>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: '#fff',
              fontSize: '20px',
              cursor: 'pointer',
              padding: '4px 8px',
              borderRadius: '4px',
              opacity: 0.7
            }}
            onMouseEnter={(e) => e.target.style.opacity = '1'}
            onMouseLeave={(e) => e.target.style.opacity = '0.7'}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{
          padding: '20px',
          overflowY: 'auto',
          flex: 1
        }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', fontSize: '16px', opacity: 0.8 }}>
              Loading equipment...
            </div>
          ) : (
            <div>
              {/* Equipment Slots Section */}
              <div className="equipment-slots-section">
                <h4>Equipment Slots</h4>
                <div className="equipment-slots-grid">
                  {["head", "neck", "body", "handhelds", "feet"].map((slot) => {
                    const equippedName = equipData?.equipped[slot];
                    const itemDetail = equippedName ? storeItemsByName[equippedName] : null;
                    const invRecord = itemDetail ? inventoryMap[itemDetail.id] : null;
                    
                    return (
                      <div 
                        key={slot} 
                        className={`equipment-slot slot-${slot} ${equippedName ? 'occupied' : ''}`}
                        title={equippedName || `Empty ${slot} slot`}
                      >
                        {equippedName ? (
                          <>
                            <img 
                              src={`/${itemDetail.image}`} 
                              alt={equippedName}
                              className="equipment-item-image"
                              onClick={() => handleUnequip(itemDetail.id)}
                              style={{ cursor: swapping ? 'not-allowed' : 'pointer' }}
                            />
                            <div className="equipment-tooltip">
                              {equippedName}
                              {invRecord && ` (${invRecord.stack_amount})`}
                            </div>
                          </>
                        ) : (
                          <>
                            <div className="equipment-tooltip">
                              Empty {slot} slot
                            </div>
                          </>
                        )}
                        <div className="equipment-slot-label">{slot}</div>
                      </div>
                    );
                  })}
                </div>
                
                {/* Ring Slots Section */}
                <div className="ring-slots-section">
                  <h5 style={{ color: '#ffd700', margin: '15px 0 10px 0', textAlign: 'center' }}>Ring Slots</h5>
                  <div className="ring-slots" style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
                    {[1, 2, 3].map((ringIndex) => {
                      // For ring slots, we need special handling with slot_number
                      const ringSlot = `ring${ringIndex}`;
                      const equippedName = equipData?.equipped[ringSlot];
                      const itemDetail = equippedName ? storeItemsByName[equippedName] : null;
                      const invRecord = itemDetail ? inventoryMap[itemDetail.id] : null;

                      return (
                        <div 
                          key={`ring${ringIndex}`} 
                          className={`equipment-slot slot-ring ${equippedName ? 'occupied' : ''}`}
                          title={equippedName || `Empty ring slot ${ringIndex}`}
                          style={{ width: '60px', height: '60px' }}
                        >
                          {equippedName && itemDetail ? (
                            <>
                              <img 
                                src={`/${itemDetail.image}`} 
                                alt={equippedName}
                                className="equipment-item-image"
                                onClick={() => {
                                  // Check if this is the equipment ring (ID 28)
                                  if (itemDetail.id === 28 && ringIndex === 1) {
                                    // Cannot unequip equipment ring
                                  } else {
                                    handleUnequip(itemDetail.id);
                                  }
                                }}
                                style={{ 
                                  cursor: itemDetail.id === 28 || swapping ? 'not-allowed' : 'pointer', 
                                  width: '40px', 
                                  height: '40px',
                                  border: itemDetail.id === 28 ? '2px solid gold' : 'none'
                                }}
                              />
                              <div className="equipment-tooltip">
                                {equippedName}
                                {invRecord && ` (${invRecord.stack_amount})`}
                                {itemDetail.id === 28 && " (Cannot unequip)"}
                              </div>
                            </>
                          ) : (
                            <>
                              <div className="equipment-tooltip">
                                Empty ring slot {ringIndex}
                              </div>
                              <div style={{ 
                                position: 'absolute', 
                                top: '50%', 
                                left: '50%', 
                                transform: 'translate(-50%, -50%)', 
                                fontSize: '1.2rem', 
                                fontWeight: 'bold', 
                                color: '#ffd700' 
                              }}>
                                {ringIndex}
                              </div>
                            </>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>

              {/* Available Items Section */}
              <div className="available-items-section">
                <h4>Available Equipment</h4>
                <div className="items-grid">
                  {availableItems.length > 0 ? (
                    availableItems
                      .filter(item => item.amount > 0)
                      .map((item) => {
                        const itemDetail = storeItems.find(s => s.id === item.id);
                        if (!itemDetail) return null;
                        
                        // Determine item slot
                        let itemSlot = item.slot;
                        if (!itemSlot) {
                          ["head", "handhelds", "feet", "neck", "body", "ring"].forEach((s) => {
                            if (itemDetail[s] > 0) itemSlot = s;
                          });
                        }

                        // Special handling for ring items
                        let slotAvailable = true;
                        if (itemSlot === 'ring') {
                          const isEquipmentRing = itemDetail.id === 28; // Equipment ring ID
                          
                          if (isEquipmentRing) {
                            // Equipment ring can only go in slot 1
                            const isRing1Equipped = !!equipData.equipped['ring1'];
                            slotAvailable = !isRing1Equipped || equipData.equipped['ring1'] === itemDetail.name;
                          } else {
                            // Check if there's at least one available slot (ring1, ring2 or ring3)
                            const isRing1Occupied = !!equipData.equipped['ring1'];
                            const isRing2Occupied = !!equipData.equipped['ring2'];
                            const isRing3Occupied = !!equipData.equipped['ring3'];
                            
                            // Check if this ring is already equipped in any slot
                            const isAlreadyEquipped = 
                              (equipData.equipped['ring1'] === itemDetail.name) ||
                              (equipData.equipped['ring2'] === itemDetail.name) ||
                              (equipData.equipped['ring3'] === itemDetail.name);
                            
                            // Available if already equipped or if there's at least one free slot
                            slotAvailable = isAlreadyEquipped || !(isRing1Occupied && isRing2Occupied && isRing3Occupied);
                          }
                        } else {
                          // For other equipment types, check normally
                          slotAvailable = !equipData?.equipped[itemSlot] || 
                                         equipData.equipped[itemSlot] === itemDetail.name;
                        }
                        
                        // Check max stack
                        const maxStackReached = item.max_stack_amount > 0 && 
                                              item.stack_amount >= item.max_stack_amount;
                        
                        const canEquip = slotAvailable && !maxStackReached;
                        
                        return (
                          <div 
                            key={item.id} 
                            className={`available-item ${canEquip ? 'can-equip' : ''}`}
                          >
                            <img 
                              src={`/${itemDetail.image}`} 
                              alt={itemDetail.name}
                              className="item-image"
                            />
                            <div className="item-info">
                              <span className="item-name">{itemDetail.name}</span>
                              <span className="item-quantity">×{item.amount}</span>
                              <span className="item-slot">{itemSlot}</span>
                            </div>
                            <button 
                              className="equip-button"
                              onClick={() => handleSwap(item.id)}
                              disabled={swapping || !canEquip}
                            >
                              {swapping ? "..." : 
                               !slotAvailable ? 'Slot Occupied' : 
                               maxStackReached ? 'Max Equipped' : 'Equip'}
                            </button>
                          </div>
                        );
                      })
                  ) : (
                    <div className="no-items-available">
                      <span>No equipment available</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeldItemQuickSwap;
